import {observable, transaction} from 'mobx';
import {TableDataForChannelResponse} from '../../types/api/tableData/TableDataForChannel';
import _ from 'lodash';
import {ProductDiagramInstruction, SearchDiagramInstruction} from '../../stores/OdtExportStore';
import {stickyRowSums, tableDataForChannel} from '../../apis/TableAPI';
import {ProductKPI, SearchtermKPI} from '../../kpi';
import {TableSortDirection} from '../../types/Table';
import {AnalyticsTab} from '../../stores/store';
import {TablePreviewInBlockProps} from '../views/PaperSheetBlocks/TablePreview';
import {DateRange} from '../../stores/TimeIntervalStore';
import {DateUtils} from '../../utility/DateUtils';
import {AxiosResponse} from 'axios';

export interface TablePreviewState {
    dataLoading: boolean;
    data: TableDataForChannelResponse[];
    checkForTableUpdate: (newProps: TablePreviewInBlockProps, oldProps?: TablePreviewInBlockProps) => void;
}

export default class TablePreviewStore implements TablePreviewState {
    @observable public dataLoading = false;
    @observable public data: TableDataForChannelResponse[] = [];

    public checkForTableUpdate(newProps: TablePreviewInBlockProps, oldProps?: TablePreviewInBlockProps) {
        if (newProps.diagramCompanionMode) {
            this.checkForCompanionTableUpdate(newProps, oldProps);
        } else {
            this.checkForDefaultTableUpdate(newProps, oldProps);
        }
    }

    public checkForCompanionTableUpdate(newProps: TablePreviewInBlockProps, oldProps?: TablePreviewInBlockProps) {
        if (!oldProps) {
            this.updateCompanionTableData(newProps);
            return;
        }
        const {diagramInstruction: newDiagramInstructions} = newProps.mergedBuildingBlockInstructions;
        const {diagramInstruction: oldDiagramInstructions} = oldProps.mergedBuildingBlockInstructions;

        if (newDiagramInstructions && oldDiagramInstructions) {
            if (
                !_.isEqual(newDiagramInstructions.searchDiagrams, oldDiagramInstructions.searchDiagrams) ||
                !_.isEqual(newDiagramInstructions.productDiagrams, oldDiagramInstructions.productDiagrams) ||
                !_.isEqual(newProps.dateRange, oldProps.dateRange) ||
                !_.isEqual(newProps.compareDateRange, oldProps.compareDateRange) ||
                newProps.targetCurrency !== oldProps.targetCurrency
            ) {
                this.updateCompanionTableData(newProps);
                return;
            }
        }
    }

    private checkForDefaultTableUpdate(newProps: TablePreviewInBlockProps, oldProps?: TablePreviewInBlockProps) {
        if (!oldProps) {
            this.updateTableData(newProps);
            return;
        }

        const {tableInstruction: newTableInstructions} = newProps.mergedBuildingBlockInstructions;
        const {tableInstruction: oldTableInstructions} = oldProps.mergedBuildingBlockInstructions;

        if (newTableInstructions && oldTableInstructions) {
            if (
                !_.isEqual(newTableInstructions.activeViews, oldTableInstructions.activeViews) ||
                !_.isEqual(newTableInstructions.channels, oldTableInstructions.channels) ||
                !_.isEqual(newTableInstructions.filters, oldTableInstructions.filters) ||
                !_.isEqual(newTableInstructions.searches, oldTableInstructions.searches) ||
                !_.isEqual(newTableInstructions.sortings, oldTableInstructions.sortings) ||
                !_.isEqual(newProps.dateRange, oldProps.dateRange) ||
                newProps.lineLimit !== oldProps.lineLimit ||
                newProps.targetCurrency !== oldProps.targetCurrency
            ) {
                this.updateTableData(newProps);
                return;
            }
        }
    }

    private loadSearchDiagramTableRows(
        searchDiagrams: SearchDiagramInstruction[],
        intervals: DateRange[],
        targetCurrency?: string
    ): Array<Promise<AxiosResponse<TableDataForChannelResponse[]>>> {
        const uniqueSearchDiagrams = _.uniqWith(searchDiagrams, (a, b) => a.channel === b.channel && a.searchterm === b.searchterm);
        return uniqueSearchDiagrams
            .map((searchDiagram) => {
                return intervals.map((interval) => {
                    if (searchDiagram.searchterm) {
                        return tableDataForChannel({
                            targetCurrency,
                            interval: DateUtils.toDailyDateRange(interval),
                            mode: 'searchterm',
                            sorting: {attribute: SearchtermKPI.SalesFromSearches, sortDirection: TableSortDirection.DESC},
                            channel: searchDiagram.channel,
                            searchExactly: true,
                            filter: JSON.stringify([]),
                            limit: 1,
                            search: searchDiagram.searchterm,
                        });
                    } else {
                        return stickyRowSums({
                            targetCurrency,
                            interval: DateUtils.toDailyDateRange(interval),
                            channel: searchDiagram.channel,
                            mode: 'searchterm',
                            filter: JSON.stringify([]),
                            search: '',
                        });
                    }
                });
            })
            .reduce(
                (
                    acc: Array<Promise<AxiosResponse<TableDataForChannelResponse[]>>>,
                    promise: Array<Promise<AxiosResponse<TableDataForChannelResponse[]>>>
                ) => {
                    return [...acc, ...promise];
                },
                []
            );
    }

    private loadProductDiagramTableRows(
        productDiagrams: ProductDiagramInstruction[],
        intervals: DateRange[],
        targetCurrency?: string
    ): Array<Promise<AxiosResponse<TableDataForChannelResponse[]>>> {
        const uniqueProductDiagrams = _.uniqWith(productDiagrams, (a, b) => a.channel === b.channel && a.productId === b.productId);
        return uniqueProductDiagrams
            .map((productDiagram) => {
                return intervals.map((interval) => {
                    if (productDiagram.productId) {
                        return tableDataForChannel({
                            targetCurrency,
                            interval: DateUtils.toDailyDateRange(interval),
                            mode: 'product',
                            sorting: {attribute: ProductKPI.Sales, sortDirection: TableSortDirection.DESC},
                            channel: productDiagram.channel,
                            searchExactly: true,
                            filter: JSON.stringify([]),
                            limit: 1,
                            search: productDiagram.productId,
                        });
                    } else {
                        return stickyRowSums({
                            targetCurrency,
                            interval: DateUtils.toDailyDateRange(interval),
                            channel: productDiagram.channel,
                            mode: 'product',
                            filter: JSON.stringify([]),
                            search: '',
                        });
                    }
                });
            })
            .reduce(
                (
                    acc: Array<Promise<AxiosResponse<TableDataForChannelResponse[]>>>,
                    promise: Array<Promise<AxiosResponse<TableDataForChannelResponse[]>>>
                ) => {
                    return [...acc, ...promise];
                },
                []
            );
    }

    private updateCompanionTableData(propsToUse: TablePreviewInBlockProps) {
        const {tableInstruction, diagramInstruction} = propsToUse.mergedBuildingBlockInstructions;
        const {activeViews} = tableInstruction ? tableInstruction : {activeViews: []};
        const {searchDiagrams, productDiagrams} = diagramInstruction ? diagramInstruction : {searchDiagrams: [], productDiagrams: []};

        if (
            activeViews.length === 1 &&
            (searchDiagrams.length > 0 || productDiagrams.length > 0) &&
            (searchDiagrams.length === 0 || productDiagrams.length === 0)
        ) {
            this.dataLoading = true;

            const dateRanges = propsToUse.compareDateRange ? [propsToUse.dateRange, propsToUse.compareDateRange] : [propsToUse.dateRange];

            Promise.all([
                ...this.loadSearchDiagramTableRows(searchDiagrams, dateRanges, propsToUse.targetCurrency),
                ...this.loadProductDiagramTableRows(productDiagrams, dateRanges, propsToUse.targetCurrency),
            ]).then((resps) => {
                this.dataLoading = false;
                this.data = resps.map((resp, respIndex) => {
                    const isCompareDiagram = (): boolean => {
                        if (!propsToUse.compareDateRange) {
                            return false;
                        }
                        return respIndex % 2 === 1;
                    };
                    return {...resp.data[0], isCompareRangeLine: isCompareDiagram()};
                });
            });
        } else {
            transaction(() => {
                this.dataLoading = false;
                this.data = [];
            });
            throw Error('Cannot get companion table data for analytics tab ' + activeViews[0]);
        }
    }

    private updateTableData = (propsToUse: TablePreviewInBlockProps) => {
        if (!propsToUse.mergedBuildingBlockInstructions.tableInstruction) {
            throw Error('Table preview requires table instructions');
        }

        const {activeViews, filters, searches, sortings, channels} = propsToUse.mergedBuildingBlockInstructions.tableInstruction;

        if (activeViews[0] === AnalyticsTab.SearchView || activeViews[0] === AnalyticsTab.ProductView) {
            this.dataLoading = true;
            tableDataForChannel({
                targetCurrency: propsToUse.targetCurrency,
                interval: DateUtils.toDailyDateRange(propsToUse.dateRange),
                mode: activeViews[0] === AnalyticsTab.SearchView ? 'searchterm' : 'product',
                channel: channels[0],
                search: searches.length > 0 ? searches[0] : '',
                sorting:
                    sortings.length > 0
                        ? sortings[0]
                        : {
                              attribute: activeViews[0] === AnalyticsTab.SearchView ? SearchtermKPI.SalesFromSearches : ProductKPI.Sales,
                              sortDirection: TableSortDirection.DESC,
                          },
                limit: propsToUse.lineLimit ? propsToUse.lineLimit : 10,
                filter: JSON.stringify(filters),
            }).then((resp) => {
                transaction(() => {
                    this.dataLoading = false;
                    this.data = resp.data;
                });
            });
        } else {
            transaction(() => {
                this.dataLoading = false;
                this.data = [];
            });
            throw Error('Cannot get table data for analytics tab ' + activeViews);
        }
    };
}



// WEBPACK FOOTER //
// ./src/report/stores/TablePreviewStore.ts